import React, { useEffect, useRef } from "react";
import styles from "./PopupCard.module.scss";
import { createFocusTrap, FocusTrap } from "focus-trap";
import { Button } from "../Button";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export type PopupCardProps = {
  onClose?: () => void;
  open: boolean;
  children: React.ReactNode;
  whiteBackground?: boolean;
  wide?: boolean;
  nestedPopup?: boolean;
  titleId?: string;
};

export function PopupCard({
  onClose,
  children,
  whiteBackground,
  wide,
  open,
  nestedPopup,
  titleId,
}: PopupCardProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const focusTrap = useRef<FocusTrap | null>(null);

  useEffect(() => {
    if (open && modalRef.current) {
      focusTrap.current = createFocusTrap(modalRef.current, {
        escapeDeactivates: !!onClose,
        allowOutsideClick: !!onClose,
        fallbackFocus: modalRef.current,
      });

      focusTrap.current.activate();

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose?.();
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        focusTrap.current?.deactivate();
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [open, !!onClose]);

  return (
    <AnimatePresence mode="sync">
      {open && (
        <motion.div
          className={`${styles.popupCardWrapper} ${wide ? styles.wide : ""} ${nestedPopup ? styles.nestedPopup : ""}`}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          <motion.div
            className={`${styles.popupCard} ${whiteBackground ? styles.whiteBackground : ""} ${wide ? styles.wide : ""} ${nestedPopup ? styles.nestedPopup : ""}`}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
            }}
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
          >
            {onClose && (
              <Button
                dark
                icon="close"
                circular
                className={styles.closeButton}
                onClick={onClose}
              >
                <span className="sr-only">{t("CLOSE", "Close")}</span>
              </Button>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
